import React, { useMemo, useState } from 'react'
import { Container, Input, Button } from 'reactstrap'
import Breadcrumbs from '../../components/Common/Breadcrumb'
import INVENTORY, { GROUPS } from './inventory'
import LayoutPreview, { resolvePreviewKind, previewKindLabel, REAL_PATTERN_CODES } from './LayoutPreview'
import ComponentMount from './ComponentMount'
import './uiPlayground.css'

/**
 * REVERT para silhueta-only (como antes do mount real):
 *   mude USE_REAL_MOUNTS para false
 *   (LayoutPreview continua intacto; ComponentMount/demoRegistry podem ficar.)
 */
const USE_REAL_MOUNTS = false

function UsedInList({ paths }) {
  const [expanded, setExpanded] = useState(false)
  if (!paths || !paths.length) {
    return (
      <p className="ui-pg-used ui-pg-used--empty">
        Usado em: <em>nenhum import encontrado</em>
      </p>
    )
  }
  const limit = 6
  const shown = expanded ? paths : paths.slice(0, limit)
  const rest = paths.length - shown.length

  return (
    <div className="ui-pg-used">
      <div className="ui-pg-used__label">
        Usado em (código)
        <span className="ui-pg-used__count">{paths.length}</span>
      </div>
      <ul className="ui-pg-used__list">
        {shown.map((p) => (
          <li key={p}><code>{p}</code></li>
        ))}
      </ul>
      {paths.length > limit && (
        <button
          type="button"
          className="ui-pg-used__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Mostrar menos' : `+${rest} mais`}
        </button>
      )}
    </div>
  )
}

function AppearsOnList({ places }) {
  const [expanded, setExpanded] = useState(false)
  if (!places || !places.length) {
    return (
      <p className="ui-pg-used ui-pg-used--empty">
        No site: <em>não ligado a nenhuma rota (ou só via util morto)</em>
      </p>
    )
  }
  const limit = 8
  const shown = expanded ? places : places.slice(0, limit)
  const rest = places.length - shown.length
  const isChrome = places.length === 1 && String(places[0].rota).startsWith('(quase')

  return (
    <div className="ui-pg-used ui-pg-appears">
      <div className="ui-pg-used__label">
        No site (rotas)
        <span className="ui-pg-used__count">{isChrome ? 'global' : places.length}</span>
      </div>
      <ul className="ui-pg-used__list ui-pg-appears__list">
        {shown.map((p) => (
          <li key={p.rota + (p.titulo || '')}>
            {isChrome ? (
              <span>{p.titulo} — {p.rota}</span>
            ) : (
              <>
                <code className="ui-pg-route">{p.rota}</code>
                {p.titulo ? <span className="ui-pg-route-title">{p.titulo}</span> : null}
              </>
            )}
          </li>
        ))}
      </ul>
      {places.length > limit && (
        <button
          type="button"
          className="ui-pg-used__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Mostrar menos' : `+${rest} mais`}
        </button>
      )}
    </div>
  )
}

function ComponentCard({ item, forceLoad }) {
  const previewKind = REAL_PATTERN_CODES.has(item.codigo)
    ? 'pattern'
    : resolvePreviewKind(item)

  return (
    <article id={item.codigo} className={`ui-pg-card ui-pg-card--${previewKind}`}>
      <header className="ui-pg-card__head">
        <div className="ui-pg-card__id">
          <span className="ui-pg-num">{item.id}</span>
          <span className="ui-pg-codigo">{item.codigo}</span>
        </div>
        <div className="ui-pg-card__titles">
          <h3>{item.nome}</h3>
          <code className="ui-pg-path">{item.arquivo}</code>
          <UsedInList paths={item.usadoEm} />
          <AppearsOnList places={item.apareceEm} />
        </div>
        <span className={`ui-pg-badge ui-pg-badge--${USE_REAL_MOUNTS ? 'live' : previewKind}`}>
          {USE_REAL_MOUNTS ? 'componente real' : previewKindLabel(previewKind)}
        </span>
      </header>
      <div className="ui-pg-card__body">
        {USE_REAL_MOUNTS
          ? <ComponentMount item={item} forceLoad={forceLoad} />
          : <LayoutPreview item={item} />}
      </div>
    </article>
  )
}

export default function UiPlayground() {
  const [filter, setFilter] = useState('')
  const [grupo, setGrupo] = useState('all')
  const [forceAll, setForceAll] = useState(false)

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return INVENTORY.filter((item) => {
      if (grupo !== 'all' && item.grupoId !== grupo) return false
      if (!q) return true
      return (
        item.nome.toLowerCase().includes(q)
        || item.codigo.toLowerCase().includes(q)
        || item.arquivo.toLowerCase().includes(q)
        || String(item.id) === q
        || (item.usadoEm || []).some((p) => p.toLowerCase().includes(q))
        || (item.apareceEm || []).some((p) =>
          String(p.rota || '').toLowerCase().includes(q)
          || String(p.titulo || '').toLowerCase().includes(q))
      )
    })
  }, [filter, grupo])

  const byGroup = useMemo(() => {
    const map = new Map()
    GROUPS.forEach((g) => map.set(g.id, []))
    filtered.forEach((item) => {
      if (!map.has(item.grupoId)) map.set(item.grupoId, [])
      map.get(item.grupoId).push(item)
    })
    return map
  }, [filtered])

  return (
    <div className="page-content ui-pg">
      <Container fluid>
        <Breadcrumbs
          title="Catálogo visual de componentes"
          breadcrumbItems={[
            { title: 'Início', link: '/dashboard' },
            { title: 'UI Playground' },
          ]}
        />

        <div className="ui-pg-hero">
          <div>
            <p className="ui-pg-lead">
              Listagem dos <strong>{INVENTORY.length}</strong> itens do oikos-design para
              decidir e padronizar. Layout ilustrativo + onde entra no código e, por inferência,
              em quais <strong>rotas</strong> do site isso aparece.
            </p>
          </div>
          <div className="ui-pg-toolbar">
            <Input
              type="search"
              placeholder="Filtrar por nome, CMP-00N, arquivo…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ui-pg-search"
            />
            <select
              className="form-control ui-pg-grupo"
              value={grupo}
              onChange={(e) => setGrupo(e.target.value)}
            >
              <option value="all">Todos os grupos</option>
              {GROUPS.map((g) => (
                <option key={g.id} value={g.id}>{g.title}</option>
              ))}
            </select>
            {USE_REAL_MOUNTS && (
              <Button
                color={forceAll ? 'success' : 'secondary'}
                size="sm"
                onClick={() => setForceAll((v) => !v)}
              >
                {forceAll ? 'Imports forçados' : 'Carregar todos agora'}
              </Button>
            )}
          </div>
        </div>

        <nav className="ui-pg-toc" aria-label="Índice">
          {GROUPS.map((g) => {
            const n = (byGroup.get(g.id) || []).length
            if (grupo !== 'all' && grupo !== g.id) return null
            if (n === 0 && filter) return null
            return (
              <a key={g.id} href={`#grupo-${g.id}`}>
                {g.title}
                <span>{INVENTORY.filter((i) => i.grupoId === g.id).length}</span>
              </a>
            )
          })}
        </nav>

        {GROUPS.map((g) => {
          const items = byGroup.get(g.id) || []
          if (!items.length) return null
          return (
            <section key={g.id} id={`grupo-${g.id}`} className="ui-pg-section">
              <h2 className="ui-pg-section__title">
                {g.title}
                <span className="ui-pg-section__count">{items.length}</span>
              </h2>
              <div className="ui-pg-grid">
                {items.map((item) => (
                  <ComponentCard key={item.codigo} item={item} forceLoad={forceAll} />
                ))}
              </div>
            </section>
          )
        })}

        {!filtered.length && (
          <p className="text-muted mt-4">Nenhum item com esse filtro.</p>
        )}
      </Container>
    </div>
  )
}
